import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import BufferOptionsItem from 'buffers/BufferOptionsItem';
import {getTablesByDocumentNameFromDocumentIds, TableLimitUtils} from 'notebook/performance/tableLimitUtils';
import {Table} from 'notebook/tables/constants/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {AnyStructuredTable, Note} from 'notebook/util/NoteUtilConstants';
import {GRAY_DARK, ORANGE, RED} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export const PERFORMANCE_ANALYSIS_DELAY_MS = 500;

interface TablesByDocumentName {
  [documentName: string]: Array<Table | AnyStructuredTable>;
}

interface PerformanceMonitorToggleProps {
  getTablesByDocumentName: () => TablesByDocumentName;
  openModal: () => void;
}

interface NotebookPerformanceMonitorToggleProps {
  note: Note;
  openModal: () => void;
}

interface MultiDocumentPerformanceMonitorToggleProps {
  documentIds: Array<string>;
  openModal: () => void;
}

export function MultiDocumentPerformanceMonitorToggle({
  documentIds,
  openModal,
}: MultiDocumentPerformanceMonitorToggleProps): JSX.Element {
  return (
    <PerformanceMonitorToggle
      getTablesByDocumentName={() => getTablesByDocumentNameFromDocumentIds(documentIds)}
      openModal={openModal}
    />
  );
}

export function NotebookPerformanceMonitorToggle({
  note,
  openModal,
}: NotebookPerformanceMonitorToggleProps): JSX.Element {
  return (
    <BufferOptionsItem className="u-vertical-center-items">
      <PerformanceMonitorToggle
        getTablesByDocumentName={() => ({entry: TableUtil.getTablesFromNote(note)})}
        openModal={openModal}
      />
    </BufferOptionsItem>
  );
}

function PerformanceMonitorToggle({
  getTablesByDocumentName,
  openModal,
}: PerformanceMonitorToggleProps): JSX.Element {
  const [totalFormulaCost, setTotalFormulaCost] = useState<number | null>(null);
  const getTablesByDocumentNameRef = useRef(getTablesByDocumentName);
  getTablesByDocumentNameRef.current = getTablesByDocumentName;

  useEffect(() => {
    const timer = setTimeout(() => {
      const {totalFormulaCost: cost} = TableLimitUtils.analyzePerformanceOfTables(
        getTablesByDocumentNameRef.current()
      );
      setTotalFormulaCost(cost);
    }, PERFORMANCE_ANALYSIS_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  let icon: BenchlingIconName = 'gauge-min';
  let iconColor = styles.defaultColor;
  if (totalFormulaCost != null) {
    if (TableLimitUtils.formulaCostIsOverMaximum(totalFormulaCost)) {
      icon = 'gauge-max';
      iconColor = styles.errorColor;
    } else if (TableLimitUtils.formulaCostIsInWarningRange(totalFormulaCost)) {
      icon = 'gauge';
      iconColor = styles.warningColor;
    }
  }

  return (
    <BenchlingIcon
      icon={icon}
      className={css(iconColor, styles.icon)}
      altText="Table Performance Monitor"
      onClick={openModal}
    />
  );
}

const styles = StyleSheet.create({
  warningColor: {
    color: ORANGE,
  },
  errorColor: {
    color: RED,
  },
  defaultColor: {
    color: GRAY_DARK,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    height: '16px',
    width: '16px',
    ':hover': {
      cursor: 'pointer',
    },
  },
});
